import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { questionsService } from '@/services/questions.service';
import { examsService } from '@/services/exams.service';
import { coursesService } from '@/services/courses.service';
import { Save, X, AlertCircle, Plus, Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { FieldError } from '@/components/FormError';
import Swal from 'sweetalert2';
import type { Question } from '@/types';

// Extended Question type with dynamic options
type DynamicQuestion = Omit<Question, 'options'> & {
  options: Record<string, string>;
};

export function QuestionEditForm() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNewQuestion = id === 'new';
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const { data: question, isLoading } = useQuery({
    queryKey: ['question', id],
    queryFn: async () => {
      try {
        console.log('[QuestionEditForm] Fetching question:', id);
        const data = await questionsService.getQuestion(id!);
        console.log('[QuestionEditForm] Question data received:', data);
        return data;
      } catch (error) {
        console.error('[QuestionEditForm] Failed to fetch question:', error);
        Swal.fire({
          title: 'Error Loading Question',
          text: 'Failed to load the question. Please try again.',
          icon: 'error',
          confirmButtonColor: '#dc2626',
        });
        return null;
      }
    },
    enabled: !isNewQuestion && !!id,
  });

  const { data: exams } = useQuery({
    queryKey: ['exams'],
    queryFn: async () => {
      try {
        return await examsService.getExams();
      } catch (error) {
        console.error('Failed to fetch exams:', error);
        return { exams: [] };
      }
    },
  });

  const { data: courses } = useQuery({
    queryKey: ['courses'],
    queryFn: async () => {
      try {
        return await coursesService.getCourses();
      } catch (error) {
        console.error('Failed to fetch courses:', error);
        return [];
      }
    },
  });

  const [formData, setFormData] = useState<Partial<DynamicQuestion>>({
    exam_id: '',
    topic_id: '',
    course: '',
    question_type: 'MCQ',
    difficulty: 'medium',
    cognitive_level: 'application',
    question_text: '',
    options: { A: '', B: '', C: '', D: '' },
    correct_option: 'A',
    explanation: '',
    language: 'pt-BR',
    estimated_time_seconds: 120,
    requires_calculator: false,
    generated_by: 'human',
    status: 'draft',
  });

  const [optionLetters, setOptionLetters] = useState<string[]>(['A', 'B', 'C', 'D']);

  // Determine if question type needs options
  const questionTypeNeedsOptions = (type: string) => {
    return ['MCQ', 'true_false', 'multiple_response', 'case_study'].includes(type);
  };

  const addOption = () => {
    const nextLetter = String.fromCharCode(65 + optionLetters.length); // A=65, B=66, etc.
    if (optionLetters.length < 6) { // Max 6 options (A-F)
      setOptionLetters([...optionLetters, nextLetter]);
      const newOptions: Record<string, string> = { ...formData.options, [nextLetter]: '' };
      setFormData({
        ...formData,
        options: newOptions,
      });
    }
  };

  const removeOption = (letter: string) => {
    if (optionLetters.length <= 2) {
      Swal.fire({
        title: 'Cannot Remove',
        text: 'At least 2 options are required.',
        icon: 'warning',
        confirmButtonColor: '#1e73be',
      });
      return;
    }

    const newLetters = optionLetters.filter(l => l !== letter);
    setOptionLetters(newLetters);

    const newOptions: Record<string, string> = { ...formData.options };
    delete newOptions[letter];

    // If removed option was correct, set first option as correct
    const newCorrectOption = formData.correct_option === letter ? newLetters[0] : formData.correct_option;

    setFormData({
      ...formData,
      options: newOptions,
      correct_option: newCorrectOption,
    });
  };

  // Load question data into form - handle different data formats dynamically
  useEffect(() => {
    if (question && !isNewQuestion) {
      console.log('[QuestionEditForm] Loading question into form:', question);
      const q = question as any;

      // Convert alternatives array to options object if needed
      let options: Record<string, string> = {};
      let letters: string[] = [];

      if (q.alternatives && Array.isArray(q.alternatives) && q.alternatives.length > 0) {
        // Backend format: alternatives array
        console.log('[QuestionEditForm] Converting alternatives to options');
        q.alternatives.forEach((alt: any) => {
          options[alt.letter] = alt.text;
          letters.push(alt.letter);
        });
        letters.sort(); // Sort alphabetically
      } else if (q.options && typeof q.options === 'object') {
        // Frontend format: options object
        console.log('[QuestionEditForm] Using existing options object');
        options = { ...q.options };
        letters = Object.keys(q.options).sort();
      } else {
        // Default: create empty options
        console.log('[QuestionEditForm] No options found, creating defaults');
        options = { A: '', B: '', C: '', D: '' };
        letters = ['A', 'B', 'C', 'D'];
      }

      // Find correct answer from alternatives or use existing
      let correctOption = q.correct_option || q.correct_answer || letters[0];
      if (q.alternatives && Array.isArray(q.alternatives)) {
        const correctAlt = q.alternatives.find((alt: any) => alt.isCorrect);
        if (correctAlt) {
          correctOption = correctAlt.letter;
          console.log('[QuestionEditForm] Correct answer from alternatives:', correctOption);
        }
      }

      console.log('[QuestionEditForm] Final options:', options);
      console.log('[QuestionEditForm] Option letters:', letters);
      console.log('[QuestionEditForm] Correct option:', correctOption);

      setOptionLetters(letters);
      setFormData({
        exam_id: q.exam_id || q.exam || '',
        topic_id: q.topic_id || q.oeCode || '',
        course: q.course || '',
        subtopic_id: q.subtopic_id,
        question_type: q.question_type || q.type || 'MCQ',
        difficulty: q.difficulty || 'medium',
        cognitive_level: q.cognitive_level || 'application',
        question_text: q.question_text || q.stem || '',
        options: options,
        correct_option: correctOption,
        explanation: q.explanation || '',
        // Case study fields
        caseTitle: q.caseTitle || '',
        caseContext: q.caseContext || '',
        caseData: q.caseData || {},
        subQuestionIndex: q.subQuestionIndex || undefined,
        totalSubQuestions: q.totalSubQuestions || undefined,
        language: q.language || 'pt-BR',
        estimated_time_seconds: q.estimated_time_seconds || 120,
        requires_calculator: q.requires_calculator || false,
        generated_by: q.generated_by || 'llm',
        status: q.status || 'draft',
        alternatives: q.alternatives || [],
        review_notes: q.review_notes || '',
        quality_score: q.quality_score,
        question_id: q.question_id || q._id,
      });

      console.log('[QuestionEditForm] Form data set successfully');
    }
  }, [question, isNewQuestion]);

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<Question>) => {
      if (isNewQuestion) {
        throw new Error('Creating new questions is not supported yet. Please use the Generate Questions feature.');
      }
      console.log('[QuestionEditForm] Saving question data:', data);
      const result = await questionsService.updateQuestion(id!, data as any);
      console.log('[QuestionEditForm] Save result:', result);
      return result;
    },
    onSuccess: (data) => {
      console.log('[QuestionEditForm] Question saved successfully:', data);
      queryClient.invalidateQueries({ queryKey: ['questions'] });
      queryClient.invalidateQueries({ queryKey: ['question', id] });

      Swal.fire({
        title: isNewQuestion ? 'Question Created!' : 'Question Updated!',
        text: isNewQuestion ? 'The question has been created successfully.' : 'The question has been updated successfully.',
        icon: 'success',
        timer: 2000,
        showConfirmButton: false,
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      }).then(() => {
        navigate('/questions');
      });
    },
    onError: (error: any) => {
      console.error('[QuestionEditForm] Save error:', error);
      Swal.fire({
        title: 'Save Failed',
        text: error.response?.data?.error || error.message || 'Failed to save the question. Please try again.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
        customClass: {
          popup: 'rounded-2xl',
          title: 'text-2xl font-bold text-gray-900',
        },
      });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('[QuestionEditForm] Submit triggered');
    console.log('[QuestionEditForm] Current form data:', formData);

    // Dynamic Validation
    const errors: Record<string, string> = {};

    if (!formData.question_text?.trim()) {
      errors.question_text = 'Question text is required';
    }

    if (!formData.exam_id) {
      errors.exam_id = 'Exam is required';
    }

    // Validate options only for question types that need them
    if (questionTypeNeedsOptions(formData.question_type || '')) {
      const emptyOptions = optionLetters.filter(letter => !formData.options?.[letter as keyof typeof formData.options]?.trim());
      if (emptyOptions.length > 0) {
        errors.options = `All options (${optionLetters.join(', ')}) must be filled`;
      }

      if (!formData.correct_option) {
        errors.correct_option = 'Please select the correct answer';
      }
    }

    if (!formData.explanation?.trim()) {
      errors.explanation = 'Explanation is required';
    }

    if (Object.keys(errors).length > 0) {
      console.log('[QuestionEditForm] Validation errors:', errors);
      setValidationErrors(errors);
      Swal.fire({
        title: 'Validation Error',
        html: `<p class="text-left">Please fix the following:</p><ul class="text-left list-disc pl-5 mt-2">${Object.values(errors).map(e => `<li>${e}</li>`).join('')}</ul>`,
        icon: 'warning',
        confirmButtonColor: '#1e73be',
      });
      return;
    }

    setValidationErrors({});
    console.log('[QuestionEditForm] Validation passed');

    // Look up course name from course ID
    const selectedCourse = formData.course
      ? (Array.isArray(courses) ? courses : []).find((c: any) => c.course_id === formData.course)
      : null;
    const courseName = selectedCourse ? selectedCourse.name : undefined;

    // Prepare data for submission - convert to backend format
    const submitData: any = {
      stem: formData.question_text, // Backend uses 'stem' field
      type: formData.question_type,
      difficulty: formData.difficulty,
      exam: formData.exam_id,
      exams: [formData.exam_id],
      oeCode: formData.topic_id, // CLIENT REQUIREMENT: Single OE code
      course: formData.course || undefined,
      courseName: courseName || undefined,
      explanation: formData.explanation,
      status: formData.status,
      cognitiveLevel: formData.cognitive_level,
      language: formData.language,
      estimatedTime: formData.estimated_time_seconds,
      requiresCalculator: formData.requires_calculator,
      generatedBy: formData.generated_by,
      reviewNotes: formData.review_notes,
    };

    // Convert options to alternatives array format for backend
    if (questionTypeNeedsOptions(formData.question_type || '')) {
      submitData.alternatives = optionLetters.map(letter => ({
        letter,
        text: formData.options?.[letter as keyof typeof formData.options] || '',
        isCorrect: letter === formData.correct_option,
      }));
      submitData.correctAnswer = formData.correct_option;
    }

    console.log('[QuestionEditForm] Prepared submit data:', submitData);
    saveMutation.mutate(submitData);
  };

  const handleCancelClick = () => {
    setShowCancelDialog(true);
  };

  const handleCancelConfirm = () => {
    navigate('/questions');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading question data...</p>
          <p className="text-sm text-gray-400 mt-2">Question ID: {id}</p>
        </div>
      </div>
    );
  }

  if (!isNewQuestion && !question) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Question Not Found</h2>
          <p className="text-gray-600 mb-4">The question could not be loaded.</p>
          <button onClick={() => navigate('/questions')} className="btn-primary">
            Back to Questions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            {isNewQuestion ? 'Create New Question' : 'Edit Question'}
          </h1>
          {!isNewQuestion && (
            <div className="mt-1 space-y-1">
              <p className="text-gray-600">Question ID: {id}</p>
              {formData.question_text && (
                <p className="text-sm text-green-600 flex items-center">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Question data loaded successfully
                </p>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button onClick={handleCancelClick} className="btn-secondary flex items-center">
            <X className="w-4 h-4 mr-2" />
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={saveMutation.isPending}
            className="btn-primary flex items-center"
          >
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? 'Saving...' : 'Save Question'}
          </button>
        </div>
      </div>

      {/* Debug/Info Card - Remove after testing */}
      {!isNewQuestion && formData.question_text && (
        <div className="card bg-blue-50 border-blue-200">
          <h3 className="text-sm font-semibold text-blue-900 mb-2">✓ Question Data Loaded</h3>
          <div className="text-xs text-blue-800 space-y-1">
            <p><strong>Type:</strong> {formData.question_type}</p>
            <p><strong>Difficulty:</strong> {formData.difficulty}</p>
            <p><strong>Options:</strong> {optionLetters.join(', ')}</p>
            <p><strong>Correct:</strong> {formData.correct_option}</p>
            <p><strong>Preview:</strong> {formData.question_text?.substring(0, 80)}...</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Basic Information</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Exam <span className="text-red-600">*</span>
              </label>
              <select
                value={formData.exam_id}
                onChange={(e) => setFormData({ ...formData, exam_id: e.target.value })}
                className={`input-field ${validationErrors.exam_id ? 'border-red-500' : ''}`}
                required
              >
                <option value="">Select exam...</option>
                {(Array.isArray(exams) ? exams : exams?.exams || []).map((exam: any) => (
                  <option key={exam.exam_id} value={exam.exam_id}>
                    {exam.exam_id} - {exam.name}
                  </option>
                ))}
              </select>
              <FieldError error={validationErrors.exam_id} />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course
              </label>
              <select
                value={formData.course || ''}
                onChange={(e) => setFormData({ ...formData, course: e.target.value })}
                className="input-field"
              >
                <option value="">Select course (optional)...</option>
                {(Array.isArray(courses) ? courses : []).map((course: any) => (
                  <option key={course.course_id || course.name} value={course.course_id}>
                    {course.name || course.course_id}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Question Type <span className="text-red-600">*</span>
              </label>
              <select
                value={formData.question_type}
                onChange={(e) => setFormData({ ...formData, question_type: e.target.value as any })}
                className="input-field"
                required
              >
                <option value="MCQ">Multiple Choice (MCQ)</option>
                <option value="SHORT">Short Answer</option>
                <option value="true_false">True/False</option>
                <option value="case_study">Case Study</option>
                <option value="interactive">Interactive</option>
                <option value="decision_tree">Decision Tree</option>
                <option value="multiple_response">Multiple Response</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {questionTypeNeedsOptions(formData.question_type || '')
                  ? 'This type requires multiple choice options'
                  : 'This type uses open-ended answers'}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Difficulty <span className="text-red-600">*</span>
              </label>
              <select
                value={formData.difficulty}
                onChange={(e) => setFormData({ ...formData, difficulty: e.target.value as any })}
                className="input-field"
                required
              >
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
          </div>
        </div>

        {/* Question Content */}
        <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Question Content</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question Text <span className="text-red-600">*</span>
            </label>
            <textarea
              value={formData.question_text}
              onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
              className="input-field"
              rows={4}
              required
              placeholder={formData.question_type === 'case_study'
                ? "Enter the specific sub-question that references the case context above..."
                : "Enter the question text..."}
            />
            <FieldError error={validationErrors.question_text} />
          </div>

          {/* Show options only for question types that need them */}
          {questionTypeNeedsOptions(formData.question_type || '') && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-gray-700">
                  Options <span className="text-red-600">*</span>
                </label>
                <button
                  type="button"
                  onClick={addOption}
                  disabled={optionLetters.length >= 6}
                  className="text-sm text-primary-600 hover:text-primary-700 flex items-center font-medium disabled:opacity-50"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Option
                </button>
              </div>

              {optionLetters.map((opt) => (
                <div key={opt} className="flex items-start space-x-3">
                  <div className="flex items-center mt-3">
                    <input
                      type="radio"
                      name="correct_option"
                      value={opt}
                      checked={formData.correct_option === opt}
                      onChange={(e) => setFormData({ ...formData, correct_option: e.target.value })}
                      className="w-4 h-4 text-primary-600"
                    />
                    <label className="ml-2 text-sm font-medium text-gray-700">{opt})</label>
                  </div>
                  <input
                    type="text"
                    value={formData.options?.[opt as keyof typeof formData.options] || ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        options: { ...formData.options!, [opt]: e.target.value },
                      })
                    }
                    className="flex-1 input-field"
                    required
                    placeholder={`Option ${opt}`}
                  />
                  {optionLetters.length > 2 && (
                    <button
                      type="button"
                      onClick={() => removeOption(opt)}
                      className="mt-3 text-red-600 hover:text-red-700"
                      title="Remove option"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}

              <p className="text-xs text-gray-600 mt-2">
                <AlertCircle className="w-3 h-3 inline mr-1" />
                Select the radio button to mark the correct answer. You can add up to 6 options.
              </p>
              <FieldError error={validationErrors.options} />
            </div>
          )}

          {/* For question types without options (like case_study, SHORT) */}
          {!questionTypeNeedsOptions(formData.question_type || '') && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <AlertCircle className="w-4 h-4 inline mr-2" />
                This question type ({formData.question_type}) does not require multiple choice options.
                Provide the answer in the explanation field below.
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Explanation <span className="text-red-600">*</span>
            </label>
            <textarea
              value={formData.explanation}
              onChange={(e) => setFormData({ ...formData, explanation: e.target.value })}
              className="input-field"
              rows={4}
              required
              placeholder="Provide a detailed explanation of the correct answer..."
            />
            <FieldError error={validationErrors.explanation} />
          </div>
        </div>

        {/* Additional Settings - Commented out */}
        {/* <div className="card space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">Additional Settings</h2>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Estimated Time (seconds)
              </label>
              <input
                type="number"
                value={formData.estimated_time_seconds}
                onChange={(e) =>
                  setFormData({ ...formData, estimated_time_seconds: parseInt(e.target.value) || 0 })
                }
                className="input-field"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Language</label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="input-field"
              >
                <option value="pt-BR">Portuguese (Brazil)</option>
                <option value="en-US">English (US)</option>
                <option value="es-ES">Spanish</option>
              </select>
            </div>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              checked={formData.requires_calculator || false}
              onChange={(e) => setFormData({ ...formData, requires_calculator: e.target.checked })}
              className="w-4 h-4 text-primary-600"
            />
            <label className="ml-2 text-sm text-gray-700">Requires calculator</label>
          </div>
        </div> */}

        {/* Review Information - Commented out */}
        {/* {!isNewQuestion && (
          <div className="card space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">Review Information</h2>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Review Notes</label>
              <textarea
                value={formData.review_notes || ''}
                onChange={(e) => setFormData({ ...formData, review_notes: e.target.value })}
                className="input-field"
                rows={3}
                placeholder="Add review notes or feedback..."
              />
            </div>
          </div>
        )} */}
      </form>

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showCancelDialog}
        title="Discard Changes?"
        message="Are you sure you want to discard all changes? This action cannot be undone."
        confirmText="Discard"
        cancelText="Keep Editing"
        confirmVariant="danger"
        onConfirm={handleCancelConfirm}
        onCancel={() => setShowCancelDialog(false)}
      />
    </div>
  );
}
